(() => {
  const roots = document.querySelectorAll('[data-lss]:not([data-lss-ready])');
  if (!roots.length) return;

  let loading = null;
  const loadGsap = (src) => {
    if (window.gsap) return Promise.resolve(window.gsap);
    if (window.lsLoadGsap) return window.lsLoadGsap(src);
    if (!loading) {
      loading = new Promise((resolve, reject) => {
        const tag = document.createElement('script');
        tag.src = src;
        tag.async = true;
        tag.onload = () => (window.gsap ? resolve(window.gsap) : reject());
        tag.onerror = reject;
        document.head.appendChild(tag);
      });
    }
    return loading;
  };

  const offscreen = (el) => {
    const r = el.getBoundingClientRect();
    return r.top > window.innerHeight || r.bottom < 0;
  };

  const reveal = (root) => {
    if (!('IntersectionObserver' in window)) return;
    const head = root.querySelectorAll('[data-lss-reveal]');
    const cards = root.querySelectorAll('[data-lss-item]');
    const near = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      near.disconnect();
      loadGsap(root.dataset.gsapSrc).then((gsap) => {
        if (!offscreen(root)) return;
        const mm = gsap.matchMedia();
        mm.add('(prefers-reduced-motion: no-preference)', () => {
          gsap.set(head, { autoAlpha: 0, y: 16 });
          gsap.set(cards, { autoAlpha: 0, y: 28, scale: 0.97 });
          const tl = gsap.timeline({ paused: true, defaults: { ease: 'power3.out', duration: 0.7 } });
          tl.to(head, { autoAlpha: 1, y: 0 }, 0);
          tl.to(cards, { autoAlpha: 1, y: 0, scale: 1, duration: 0.8, ease: 'expo.out', stagger: 0.06, clearProps: 'transform,opacity,visibility' }, 0.08);
          const enter = new IntersectionObserver((entries) => {
            if (!entries.some((e) => e.isIntersecting)) return;
            enter.disconnect();
            tl.play();
          }, { rootMargin: '0px 0px -12% 0px' });
          enter.observe(root);
          return () => { enter.disconnect(); tl.kill(); };
        });
      }).catch(() => {});
    }, { rootMargin: '600px 0px' });
    near.observe(root);
  };

  const scroller = (root) => {
    const track = root.querySelector('[data-lss-track]');
    const nav = root.querySelector('[data-lss-nav]');
    if (!track || !nav) return;
    const [prev, next] = nav.querySelectorAll('[data-lss-step]');
    const sync = () => {
      const max = track.scrollWidth - track.clientWidth;
      nav.hidden = max <= 2;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max - 2;
    };
    nav.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-lss-step]');
      if (!btn) return;
      const card = track.querySelector('.lss-card');
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const step = card ? card.getBoundingClientRect().width + gap : track.clientWidth;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollBy({ left: step * Number(btn.dataset.lssStep), behavior: reduce ? 'auto' : 'smooth' });
    });
    track.addEventListener('scroll', sync, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(sync).observe(track);
    sync();
  };

  const player = (root) => {
    const dialog = root.querySelector('[data-lss-dialog]');
    const items = [...root.querySelectorAll('[data-lss-open]')];
    if (!dialog || !items.length || typeof dialog.showModal !== 'function') return;
    const frame = dialog.querySelector('[data-lss-player]');
    const title = dialog.querySelector('[data-lss-title]');
    const count = dialog.querySelector('[data-lss-count]');
    const link = dialog.querySelector('[data-lss-link]');
    const slot = dialog.querySelector('[data-lss-product-slot]');
    const prev = dialog.querySelector('[data-lss-go="-1"]');
    const next = dialog.querySelector('[data-lss-go="1"]');
    const counter = root.dataset.counter || '';
    let index = -1;
    let opener = null;

    const show = (i) => {
      index = Math.max(0, Math.min(items.length - 1, i));
      const item = items[index];
      const id = encodeURIComponent(item.dataset.id);
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&playsinline=1&rel=0&modestbranding=1`;
      iframe.title = item.dataset.title || item.getAttribute('aria-label') || '';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      frame.replaceChildren(iframe);
      title.textContent = item.dataset.title || '';
      title.hidden = !item.dataset.title;
      if (count) count.textContent = items.length > 1 ? counter.replace('{index}', index + 1).replace('{total}', items.length) : '';
      if (link) link.href = item.dataset.url;
      if (slot) {
        const tpl = item.closest('[data-lss-item]')?.querySelector('template[data-lss-product]');
        slot.replaceChildren(...(tpl ? [tpl.content.cloneNode(true)] : []));
        slot.hidden = !tpl;
        dialog.classList.toggle('has-product', !!tpl);
      }
      prev.hidden = next.hidden = items.length < 2;
      prev.disabled = index === 0;
      next.disabled = index === items.length - 1;
    };

    const open = (i, btn) => {
      opener = btn;
      btn.focus({ preventScroll: true });
      show(i);
      dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
    };

    items.forEach((btn, i) => btn.addEventListener('click', () => open(i, btn)));
    dialog.addEventListener('click', (e) => {
      const go = e.target.closest('[data-lss-go]');
      if (go) return show(index + Number(go.dataset.lssGo));
      if (e.target.closest('[data-lss-close]') || e.target === dialog || e.target.classList.contains('lss-dialog__stage')) dialog.close();
    });
    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight' && index < items.length - 1) { e.preventDefault(); show(index + 1); }
      if (e.key === 'ArrowLeft' && index > 0) { e.preventDefault(); show(index - 1); }
    });
    dialog.addEventListener('close', () => {
      frame.replaceChildren();
      if (slot) { slot.replaceChildren(); slot.hidden = true; }
      document.documentElement.style.overflow = '';
      if (opener) opener.focus({ preventScroll: true });
    });
  };

  roots.forEach((root) => {
    root.dataset.lssReady = '';
    reveal(root);
    scroller(root);
    player(root);
  });
})();
